import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminAiSettings.css';
import { Save, TestTubeDiagonal, AlertTriangle, CheckCircle, Trash2, Edit, X } from 'lucide-react';

const AdminAiSettings = ({ backendUrl }) => {
  // Stan dla dostawcy
  const [provider, setProvider] = useState('google'); // Domyślnie google

  // Stany dla Google AI
  const [googleApiKey, setGoogleApiKey] = useState('');
  const [googleSelectedModel, setGoogleSelectedModel] = useState('');
  const [googleModels, setGoogleModels] = useState([]);

  // Stany dla DeepSeek
  const [deepseekApiKey, setDeepseekApiKey] = useState('');
  const [deepseekSelectedModel, setDeepseekSelectedModel] = useState('');
  const [deepseekModels, setDeepseekModels] = useState([]); // Możemy potrzebować predefiniowanej listy lub pobrać ją

  // Stany dla OpenRouter
  const [openRouterApiKey, setOpenRouterApiKey] = useState('');
  const [openRouterSelectedModel, setOpenRouterSelectedModel] = useState('');
  const [openRouterModels, setOpenRouterModels] = useState([]);
  const [openRouterReasoning, setOpenRouterReasoning] = useState(false);

  // Stany dla dostawcy obrazów AI
  const [imageProvider, setImageProvider] = useState(''); // np. 'google', 'deepseek', 'openrouter' lub puste
  const [imageSelectedModel, setImageSelectedModel] = useState(''); // Ogólny wybrany model dla obrazów (dla Google/DeepSeek)
  // Klucze API dla obrazów - mogą być takie same jak główne, ale dajemy opcję osobnych
  const [googleImageApiKey, setGoogleImageApiKey] = useState('');
  const [deepseekImageApiKey, setDeepseekImageApiKey] = useState('');
  const [openRouterImageApiKey, setOpenRouterImageApiKey] = useState('');
  const [openRouterImageSelectedModel, setOpenRouterImageSelectedModel] = useState(''); // Dedykowany dla OpenRouter Image
  // Listy modeli dla obrazów (mogą być inne niż dla tekstu)
  const [googleImageModels, setGoogleImageModels] = useState([]);
  const [deepseekImageModels, setDeepseekImageModels] = useState([]);
  const [openRouterImageModels, setOpenRouterImageModels] = useState([]); // Możemy użyć openRouterModels lub dedykowanej listy


  // Wspólne stany
  const [prompts, setPrompts] = useState([]);
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptText, setNewPromptText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: boolean, message: string, error?: string }
  const [saveStatus, setSaveStatus] = useState(''); // '', 'success', 'error'
  const [promptSaveStatus, setPromptSaveStatus] = useState(''); // '', 'success', 'error'
  const [editingPromptId, setEditingPromptId] = useState(null); // ID promptu w trybie edycji
  const [editedPromptText, setEditedPromptText] = useState(''); // Tekst edytowanego promptu
  const editTextAreaRef = useRef(null); // Ref dla textarea edycji

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setSaveStatus('');
    try {
      const response = await fetch(`${backendUrl}/admin/ai/settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setProvider(data.provider || 'google'); // Ustaw dostawcę
          // Ustaw dane dla Google
          setGoogleApiKey(data.apiKey || ''); // Zachowujemy 'apiKey' dla kompatybilności wstecznej Google
          setGoogleSelectedModel(data.selectedModel || ''); // Zachowujemy 'selectedModel' dla Google
          // Ustaw dane dla DeepSeek
          setDeepseekApiKey(data.deepseekApiKey || '');
          setDeepseekSelectedModel(data.deepseekSelectedModel || '');
          // Ustaw dane dla OpenRouter
          setOpenRouterApiKey(data.openRouterApiKey || '');
          setOpenRouterSelectedModel(data.openRouterSelectedModel || '');
          setOpenRouterReasoning(data.openRouterReasoning || false);
          // Ustawienia dla obrazów
          setImageProvider(data.imageProvider || '');
          setImageSelectedModel(data.imageSelectedModel || ''); // Dla Google/DeepSeek
          setGoogleImageApiKey(data.googleImageApiKey || '');
          setDeepseekImageApiKey(data.deepseekImageApiKey || '');
          setOpenRouterImageApiKey(data.openRouterImageApiKey || '');
          setOpenRouterImageSelectedModel(data.openRouterImageSelectedModel || ''); // Nowe pole
        } else {
          // Jeśli brak danych, ustaw domyślnego dostawcę
          setProvider('google');
          setGoogleApiKey('');
          setGoogleSelectedModel('');
          setDeepseekApiKey('');
          setDeepseekSelectedModel('');
          setOpenRouterApiKey('');
          setOpenRouterSelectedModel('');
          setOpenRouterReasoning(false);
          // Domyślne dla obrazów
          setImageProvider('');
          setImageSelectedModel('');
          setGoogleImageApiKey('');
          setDeepseekImageApiKey('');
          setOpenRouterImageApiKey('');
          setOpenRouterImageSelectedModel(''); // Nowe pole
        }
      } else {
        console.error('Błąd pobierania ustawień AI:', response.statusText);
        // W razie błędu, ustaw domyślnego dostawcę
        setProvider('google');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Błąd sieci podczas pobierania ustawień AI:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl]);

  // Zmodyfikowana funkcja pobierania modeli, uwzględniająca dostawcę i kontekst (tekst vs obraz)
  const fetchModels = useCallback(async (providerToFetch, isImageContext = false) => {
    if (!providerToFetch) return;

    setIsLoading(true);
    try {
      // Dodajemy parametr 'provider' i opcjonalnie 'imageProvider' (jeśli backend go obsługuje do filtrowania)
      // Na razie backend używa tylko 'provider', ale przekazujemy kontekst dla jasności
      let url = `${backendUrl}/admin/ai/models?provider=${providerToFetch}`;
      if (isImageContext) {
        // Można by dodać parametr ?context=image, jeśli backend by to wspierał
        // Na razie zakładamy, że backend /admin/ai/models?provider=openrouter zwróci wszystkie modele
        // a filtrowanie modeli wizualnych odbywa się po stronie klienta lub jest niejawne
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (providerToFetch === 'google') {
          if (isImageContext) setGoogleImageModels(data || []); else setGoogleModels(data || []);
        } else if (providerToFetch === 'deepseek') {
          if (isImageContext) setDeepseekImageModels(data || []); else setDeepseekModels(data || []);
        } else if (providerToFetch === 'openrouter') {
          // Dla OpenRouter, na razie używamy tej samej listy dla tekstu i obrazów,
          // ponieważ backend nie rozróżnia ich przy pobieraniu.
          // Można by dodać filtrowanie po stronie klienta, jeśli znamy modele wizualne.
          if (isImageContext) setOpenRouterImageModels(data || []); else setOpenRouterModels(data || []);
        }
      } else {
        console.error(`Błąd pobierania modeli dla ${providerToFetch} (kontekst obrazu: ${isImageContext}):`, response.statusText);
        if (providerToFetch === 'google') { if (isImageContext) setGoogleImageModels([]); else setGoogleModels([]); }
        else if (providerToFetch === 'deepseek') { if (isImageContext) setDeepseekImageModels([]); else setDeepseekModels([]); }
        else if (providerToFetch === 'openrouter') { if (isImageContext) setOpenRouterImageModels([]); else setOpenRouterModels([]); }
      }
    } catch (error) {
      console.error(`Błąd sieci podczas pobierania modeli dla ${providerToFetch} (kontekst obrazu: ${isImageContext}):`, error);
      if (providerToFetch === 'google') { if (isImageContext) setGoogleImageModels([]); else setGoogleModels([]); }
      else if (providerToFetch === 'deepseek') { if (isImageContext) setDeepseekImageModels([]); else setDeepseekModels([]); }
      else if (providerToFetch === 'openrouter') { if (isImageContext) setOpenRouterImageModels([]); else setOpenRouterModels([]); }
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, provider]); // Zależność od providera

  const fetchPrompts = useCallback(async () => {
    setIsLoading(true);
    setPromptSaveStatus('');
    try {
      const response = await fetch(`${backendUrl}/admin/ai/prompts`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPrompts(data || []);
      } else {
        console.error('Błąd pobierania promptów AI:', response.statusText);
        setPromptSaveStatus('error');
      }
    } catch (error) {
      console.error('Błąd sieci podczas pobierania promptów AI:', error);
      setPromptSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl]);

  // Efekt do pobierania danych początkowych i modeli przy zmianie dostawcy
  useEffect(() => {
    fetchSettings(); // Pobierz ustawienia (w tym dostawcę)
    fetchPrompts(); // Prompty są niezależne od dostawcy
  }, [fetchSettings, fetchPrompts]);

  useEffect(() => {
    // Pobierz modele dla aktualnie wybranego dostawcy tekstowego
    if (provider) {
      fetchModels(provider, false);
    }
    // Pobierz modele dla aktualnie wybranego dostawcy obrazów
    if (imageProvider) {
      fetchModels(imageProvider, true);
    }
    // Resetuj wynik testu przy zmianie dostawcy
    setTestResult(null);
  }, [provider, imageProvider, fetchModels]);


  const handleSaveSettings = async () => {
    setIsLoading(true);
    setSaveStatus('');
    setTestResult(null);
    try {
      // Przygotuj dane do wysłania w zależności od dostawcy
      const settingsData = {
        provider,
        apiKey: googleApiKey, // Google
        selectedModel: googleSelectedModel, // Google
        deepseekApiKey: deepseekApiKey, // DeepSeek
        deepseekSelectedModel: deepseekSelectedModel, // DeepSeek
        openRouterApiKey: openRouterApiKey, // OpenRouter
        openRouterSelectedModel: openRouterSelectedModel, // OpenRouter
        openRouterReasoning: openRouterReasoning, // OpenRouter
        // Ustawienia dla obrazów
        imageProvider: imageProvider,
        imageSelectedModel: imageSelectedModel, // Dla Google/DeepSeek
        googleImageApiKey: googleImageApiKey,
        deepseekImageApiKey: deepseekImageApiKey,
        openRouterImageApiKey: openRouterImageApiKey,
        openRouterImageSelectedModel: openRouterImageSelectedModel, // Nowe pole
      };

      const response = await fetch(`${backendUrl}/admin/ai/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsData),
        credentials: 'include',
      });
      if (response.ok) {
        setSaveStatus('success');
        // Po zapisie odśwież modele dla aktywnego dostawcy
        fetchModels(provider);
      } else {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error('Błąd zapisu ustawień AI:', response.statusText, errorData);
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Błąd sieci podczas zapisu ustawień AI:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
      // Hide status message after a few seconds
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleTestConnection = async () => {
    setIsLoading(true);
    setTestResult(null);
    setSaveStatus('');
    try {
      // Dodajemy parametr 'provider' do zapytania testowego
      // TODO: Rozbudować testowanie o dostawcę obrazów, jeśli jest inny niż główny
      const currentProviderToTest = provider; // Na razie testujemy tylko głównego dostawcę
      const response = await fetch(`${backendUrl}/admin/ai/test?provider=${currentProviderToTest}`, { credentials: 'include' });
      const data = await response.json();
      let providerName = '';
      if (currentProviderToTest === 'google') providerName = 'Google AI';
      else if (currentProviderToTest === 'deepseek') providerName = 'DeepSeek';
      else if (currentProviderToTest === 'openrouter') providerName = 'OpenRouter';

      if (response.ok) {
        setTestResult({ success: true, message: data.message || `Połączenie z ${providerName} OK` });
      } else {
        setTestResult({ success: false, message: data.message || `Błąd testu ${providerName}`, error: data.error });
      }
    } catch (error) {
      console.error(`Błąd sieci podczas testowania połączenia ${provider}:`, error); // Logujemy głównego providera
      setTestResult({ success: false, message: 'Błąd sieci podczas testu', error: error.toString() });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPrompt = async (e) => {
    e.preventDefault();
    if (!newPromptName || !newPromptText) {
      alert('Nazwa i treść prompta są wymagane.');
      return;
    }
    setIsLoading(true);
    setPromptSaveStatus('');
    try {
      const response = await fetch(`${backendUrl}/admin/ai/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptName: newPromptName, promptText: newPromptText }),
        credentials: 'include',
      });
      if (response.ok) {
        setNewPromptName('');
        setNewPromptText('');
        setPromptSaveStatus('success');
        fetchPrompts(); // Refresh prompts list
      } else {
        const errorData = await response.json();
        console.error('Błąd zapisu prompta AI:', response.statusText, errorData);
        setPromptSaveStatus('error');
      }
    } catch (error) {
      console.error('Błąd sieci podczas zapisu prompta AI:', error);
      setPromptSaveStatus('error');
    } finally {
      setIsLoading(false);
      setTimeout(() => setPromptSaveStatus(''), 3000);
    }
  };

  // --- NOWE Funkcje do edycji i usuwania promptów ---

  const handleEditPrompt = (prompt) => {
    setEditingPromptId(prompt.id);
    setEditedPromptText(prompt.promptText);
    // Focus na textarea po krótkim opóźnieniu, aby React zdążył wyrenderować
    setTimeout(() => {
      editTextAreaRef.current?.focus();
      editTextAreaRef.current?.select(); // Zaznacz tekst
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingPromptId(null);
    setEditedPromptText('');
  };

  const handleSaveEditedPrompt = async (promptId) => {
    if (!editedPromptText.trim()) {
      alert('Treść prompta nie może być pusta.');
      return;
    }
    setIsLoading(true);
    setPromptSaveStatus(''); // Reset statusu
    try {
      const response = await fetch(`${backendUrl}/admin/ai/prompts/${promptId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptText: editedPromptText }),
        credentials: 'include',
      });
      if (response.ok) {
        setEditingPromptId(null); // Wyjdź z trybu edycji
        setPromptSaveStatus('success');
        fetchPrompts(); // Odśwież listę
      } else {
        const errorData = await response.json();
        console.error('Błąd aktualizacji prompta:', response.statusText, errorData);
        setPromptSaveStatus('error');
        alert(`Błąd aktualizacji prompta: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error('Błąd sieci podczas aktualizacji prompta:', error);
      setPromptSaveStatus('error');
      alert('Błąd sieci podczas aktualizacji prompta.');
    } finally {
      setIsLoading(false);
      setTimeout(() => setPromptSaveStatus(''), 3000);
    }
  };

  const handleDeletePrompt = async (promptId, promptName) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć prompt "${promptName}"?`)) {
      return;
    }
    setIsLoading(true);
    setPromptSaveStatus(''); // Reset statusu
    try {
      const response = await fetch(`${backendUrl}/admin/ai/prompts/${promptId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) { // Sprawdź status 204 No Content lub 200 OK
        setPromptSaveStatus('success');
        fetchPrompts(); // Odśwież listę
      } else {
        const errorData = await response.json().catch(() => ({ message: response.statusText })); // Obsłuż brak JSON
        console.error('Błąd usuwania prompta:', response.statusText, errorData);
        setPromptSaveStatus('error');
        alert(`Błąd usuwania prompta: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error('Błąd sieci podczas usuwania prompta:', error);
      setPromptSaveStatus('error');
      alert('Błąd sieci podczas usuwania prompta.');
    } finally {
      setIsLoading(false);
      setTimeout(() => setPromptSaveStatus(''), 3000);
    }
  };

  // --- Koniec nowych funkcji ---

  // Handler zmiany dostawcy
  const handleProviderChange = (event) => {
    const newProvider = event.target.value;
    setProvider(newProvider);
  };

  // Handler zmiany dostawcy obrazów
  const handleImageProviderChange = (event) => {
    const newImageProvider = event.target.value;
    setImageProvider(newImageProvider);
    // Jeśli wybrano konkretnego dostawcę obrazów, pobierz dla niego modele
    if (newImageProvider) {
      fetchModels(newImageProvider);
    }
  };


  return (
    <div className="admin-ai-settings">
      <h2>Ustawienia Integracji AI</h2>

      {isLoading && <div className="loading-indicator">Ładowanie...</div>}

      <div className="settings-section">
        <h3>Dostawca AI (dla modyfikacji projektu tekstowego)</h3>
        <div className="form-group provider-selector">
          <label>
            <input
              type="radio"
              name="provider"
              value="google"
              checked={provider === 'google'}
              onChange={handleProviderChange}
              disabled={isLoading}
            /> Google AI
          </label>
          <label>
            <input
              type="radio"
              name="provider"
              value="deepseek"
              checked={provider === 'deepseek'}
              onChange={handleProviderChange}
              disabled={isLoading}
            /> DeepSeek
          </label>
          <label>
            <input
              type="radio"
              name="provider"
              value="openrouter"
              checked={provider === 'openrouter'}
              onChange={handleProviderChange}
              disabled={isLoading}
            /> OpenRouter
          </label>
        </div>
      </div>

      {/* Sekcja Google AI (dla tekstu) */}
      {provider === 'google' && (
        <div className="settings-section provider-settings google-settings">
          <h3>Ustawienia Google AI (Tekst)</h3>
          <div className="form-group">
            <label htmlFor="googleApiKey">Google AI API Key:</label>
            <input
              type="password"
              id="googleApiKey"
              value={googleApiKey}
              onChange={(e) => setGoogleApiKey(e.target.value)}
              placeholder="Wklej klucz Google AI API"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="googleSelectedModel">Wybrany Model Google AI:</label>
            <select
              id="googleSelectedModel"
              value={googleSelectedModel}
              onChange={(e) => setGoogleSelectedModel(e.target.value)}
              disabled={isLoading || googleModels.length === 0}
            >
              <option value="">-- Wybierz model Google --</option>
              {googleModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.id})
                </option>
              ))}
            </select>
            {googleModels.length === 0 && !isLoading && <span className="warning"> Nie udało się pobrać modeli Google. Sprawdź klucz API.</span>}
          </div>
        </div>
      )}

      {/* Sekcja DeepSeek (dla tekstu) */}
      {provider === 'deepseek' && (
        <div className="settings-section provider-settings deepseek-settings">
          <h3>Ustawienia DeepSeek (Tekst)</h3>
          <div className="form-group">
            <label htmlFor="deepseekApiKey">DeepSeek API Key:</label>
            <input
              type="password"
              id="deepseekApiKey"
              value={deepseekApiKey}
              onChange={(e) => setDeepseekApiKey(e.target.value)}
              placeholder="Wklej klucz DeepSeek API"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="deepseekSelectedModel">Wybrany Model DeepSeek:</label>
            <select
              id="deepseekSelectedModel"
              value={deepseekSelectedModel}
              onChange={(e) => setDeepseekSelectedModel(e.target.value)}
              disabled={isLoading || deepseekModels.length === 0}
            >
              <option value="">-- Wybierz model DeepSeek --</option>
              {/* Tutaj można dodać predefiniowane modele DeepSeek lub pobrane z API */}
              {deepseekModels.map((model) => (
                 <option key={model.id} value={model.id}>
                   {model.name} ({model.id})
                 </option>
               ))}
               {/* Przykładowe predefiniowane, jeśli API nie zwraca listy */}
               {deepseekModels.length === 0 && !isLoading && (
                 <>
                   <option value="deepseek-chat">deepseek-chat</option>
                   <option value="deepseek-coder">deepseek-coder</option>
                 </>
               )}
            </select>
             {deepseekModels.length === 0 && !isLoading && <span className="warning"> Nie udało się pobrać modeli DeepSeek (lub lista jest predefiniowana).</span>}
          </div>
        </div>
      )}

      {/* Sekcja OpenRouter (dla tekstu) */}
      {provider === 'openrouter' && (
        <div className="settings-section provider-settings openrouter-settings">
          <h3>Ustawienia OpenRouter (Tekst)</h3>
          <div className="form-group">
            <label htmlFor="openRouterApiKey">OpenRouter API Key:</label>
            <input
              type="password"
              id="openRouterApiKey"
              value={openRouterApiKey}
              onChange={(e) => setOpenRouterApiKey(e.target.value)}
              placeholder="Wklej klucz OpenRouter API (np. sk-or-...) "
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="openRouterSelectedModel">Wybrany Model OpenRouter:</label>
            <select
              id="openRouterSelectedModel"
              value={openRouterSelectedModel}
              onChange={(e) => setOpenRouterSelectedModel(e.target.value)}
              disabled={isLoading || openRouterModels.length === 0}
            >
              <option value="">-- Wybierz model OpenRouter --</option>
              {openRouterModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.id})
                </option>
              ))}
            </select>
            {openRouterModels.length === 0 && !isLoading && <span className="warning"> Nie udało się pobrać modeli OpenRouter. Sprawdź klucz API.</span>}
          </div>
          <div className="form-group">
            <label htmlFor="openRouterReasoning">
              <input
                type="checkbox"
                id="openRouterReasoning"
                checked={openRouterReasoning}
                onChange={(e) => setOpenRouterReasoning(e.target.checked)}
                disabled={isLoading}
              />
              Włącz tryb "Reasoning" (jeśli model wspiera)
            </label>
          </div>
        </div>
      )}

      {/* Wspólne przyciski i statusy */}
      <div className="settings-section common-actions">
         <div className="button-group">
           <button onClick={handleSaveSettings} disabled={isLoading}>
             <Save size={16} /> Zapisz Wszystkie Ustawienia AI
           </button>
           <button
             onClick={handleTestConnection}
             disabled={isLoading || 
                       (provider === 'google' && !googleApiKey) || 
                       (provider === 'deepseek' && !deepseekApiKey) || 
                       (provider === 'openrouter' && !openRouterApiKey)}
           >
             <TestTubeDiagonal size={16} /> Testuj Połączenie ({provider})
           </button>
        </div>
        {saveStatus === 'success' && <p className="status-message success">Ustawienia zapisane pomyślnie.</p>}
        {saveStatus === 'error' && <p className="status-message error">Błąd zapisu ustawień.</p>}
        {testResult && (
          <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
            <p>Wynik testu ({provider}): {testResult.message}</p>
            {testResult.error && <p className="error-details">Szczegóły: {testResult.error}</p>}
          </div>
        )}
      </div>

      {/* USTAWIENIA DLA ANALIZY OBRAZÓW */}
      <div className="settings-section image-ai-settings">
        <h3>Dostawca AI (dla analizy obrazów)</h3>
        <div className="form-group provider-selector">
          <label>
            <input type="radio" name="imageProvider" value="" checked={!imageProvider} onChange={handleImageProviderChange} disabled={isLoading} />
            Użyj ustawień głównego dostawcy ({provider})
          </label>
          <label>
            <input type="radio" name="imageProvider" value="google" checked={imageProvider === 'google'} onChange={handleImageProviderChange} disabled={isLoading} />
            Google AI (Vision)
          </label>
          <label>
            <input type="radio" name="imageProvider" value="openrouter" checked={imageProvider === 'openrouter'} onChange={handleImageProviderChange} disabled={isLoading} />
            OpenRouter (Vision)
          </label>
          {/* TODO: Dodać DeepSeek dla obrazów, jeśli będzie wspierany */}
        </div>

        {imageProvider === 'google' && (
          <div className="provider-settings google-image-settings">
            <h4>Ustawienia Google AI (Obrazy)</h4>
            <div className="form-group">
              <label htmlFor="googleImageApiKey">Google AI API Key (Obrazy):</label>
              <input
                type="password"
                id="googleImageApiKey"
                value={googleImageApiKey}
                onChange={(e) => setGoogleImageApiKey(e.target.value)}
                placeholder="Klucz API dla Google Vision (jeśli inny)"
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="googleImageSelectedModel">Wybrany Model Google AI (Obrazy):</label>
              <select
                id="googleImageSelectedModel"
                value={imageSelectedModel}
                onChange={(e) => setImageSelectedModel(e.target.value)}
                disabled={isLoading || googleModels.length === 0} // Używamy googleModels, bo Gemini Vision to też Google
              >
                <option value="">-- Wybierz model Google Vision --</option>
                {/* Filtrujemy modele Google, aby pokazać te, które mogą być modelami Vision */}
                {/* Na razie zakładamy, że wszystkie modele Google mogą być używane, można dodać bardziej szczegółowe filtrowanie */}
                {googleModels.filter(m => m.id.includes('vision') || m.id.includes('gemini-1.5') || m.id.includes('gemini-2.5')).map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} ({model.id})
                  </option>
                ))}
                 <option value="gemini-1.5-pro-latest">Gemini 1.5 Pro (Latest - Vision Capable)</option>
                 <option value="gemini-pro-vision">Gemini Pro Vision (Legacy, jeśli dostępne)</option>
              </select>
            </div>
          </div>
        )}

        {imageProvider === 'openrouter' && (
          <div className="provider-settings openrouter-image-settings">
            <h4>Ustawienia OpenRouter (Obrazy)</h4>
            <div className="form-group">
              <label htmlFor="openRouterImageApiKey">OpenRouter API Key (Obrazy):</label>
              <input
                type="password"
                id="openRouterImageApiKey"
                value={openRouterImageApiKey}
                onChange={(e) => setOpenRouterImageApiKey(e.target.value)}
                placeholder="Klucz API dla OpenRouter Vision (jeśli inny)"
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="openRouterImageSelectedModel">Wybrany Model OpenRouter (Obrazy):</label>
              <select
                id="openRouterImageSelectedModel"
                value={openRouterImageSelectedModel}
                onChange={(e) => setOpenRouterImageSelectedModel(e.target.value)}
                disabled={isLoading || openRouterImageModels.length === 0}
              >
                <option value="">-- Wybierz model OpenRouter Vision --</option>
                {/* Używamy openRouterImageModels, które na razie są takie same jak openRouterModels */}
                {/* Można dodać filtrowanie, jeśli znamy modele wizualne OpenRouter */}
                {openRouterImageModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} ({model.id})
                  </option>
                ))}
              </select>
              {openRouterImageModels.length === 0 && !isLoading && <span className="warning"> Nie udało się pobrać modeli OpenRouter. Sprawdź klucz API.</span>}
            </div>
          </div>
        )}
        {/* TODO: Dodać sekcję dla DeepSeek (obrazy), jeśli będzie potrzebna */}
      </div>


      <div className="prompts-section">
        <h3>Prompty Systemowe</h3>
        <div className="add-prompt-form">
          <h4>Dodaj nowy prompt (dla modyfikacji tekstowych)</h4>
          <form onSubmit={handleAddPrompt}>
            <div className="form-group">
              <label htmlFor="promptName">Nazwa prompta:</label>
              <input
                type="text"
                id="promptName"
                value={newPromptName}
                onChange={(e) => setNewPromptName(e.target.value)}
                placeholder="np. Analiza sentymentu"
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="promptText">Treść prompta:</label>
              <textarea
                id="promptText"
                value={newPromptText}
                onChange={(e) => setNewPromptText(e.target.value)}
                rows="4"
                placeholder="Wpisz treść prompta systemowego..."
                disabled={isLoading}
              />
            </div>
            <button type="submit" disabled={isLoading}>
              Dodaj Prompt
            </button>
            {promptSaveStatus === 'success' && <p className="status-message success">Prompt dodany pomyślnie.</p>}
            {promptSaveStatus === 'error' && <p className="status-message error">Błąd dodawania prompta.</p>}
          </form>
        </div>

        <div className="prompts-list">
          <h4>Istniejące prompty</h4>
          {prompts.length === 0 && !isLoading && <p>Brak zapisanych promptów.</p>}
          <ul>
            {prompts.map((prompt) => (
              <li key={prompt.id} className={`prompt-item ${editingPromptId === prompt.id ? 'editing' : ''}`}>
                <div className="prompt-header">
                  <strong>{prompt.promptName}</strong>
                  <div className="prompt-actions">
                    {editingPromptId === prompt.id ? (
                      <>
                        <button
                          onClick={() => handleSaveEditedPrompt(prompt.id)}
                          className="prompt-action-btn save"
                          disabled={isLoading}
                          title="Zapisz zmiany"
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="prompt-action-btn cancel"
                          disabled={isLoading}
                          title="Anuluj edycję"
                        >
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditPrompt(prompt)}
                          className="prompt-action-btn edit"
                          disabled={isLoading}
                          title="Edytuj prompt"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePrompt(prompt.id, prompt.promptName)}
                          className="prompt-action-btn delete"
                          disabled={isLoading}
                          title="Usuń prompt"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingPromptId === prompt.id ? (
                  <textarea
                    ref={editTextAreaRef}
                    value={editedPromptText}
                    onChange={(e) => setEditedPromptText(e.target.value)}
                    rows="6" // Więcej miejsca na edycję
                    className="prompt-edit-textarea"
                    disabled={isLoading}
                  />
                ) : (
                  <pre className="prompt-text-display">{prompt.promptText}</pre>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminAiSettings;
